import { coreApi } from '@/api';
import {
  ActionHandlerRemapOptions,
  buildMissionDuplicatePayload,
  buildQuestDuplicatePayload,
  cloneActionHandlerForDuplicate
} from '@/pages/missions/duplicateUtils';
import { extractQuestResponseData } from '@/pages/missions/questDataUtils';

export * from '@/pages/missions/questDataUtils';

export const MISSION_NAV_CARD_WIDTH = '160px';
export const MISSION_CARD_MAX_HEIGHT = '287px';
export const MISSION_API_VERSION_HEADER = {
  headers: {
    'x-rwd-api-version': '1.1'
  }
};

export function extractCommonResponseData<T>(payload: T | CommonResponseModel<T>): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as CommonResponseModel<T>).data;
  }

  return payload as T;
}

export function deleteHTMLTag(value: string) {
  const extractTextPattern = /(<([^>]+)>)/gi;
  return value.replace(extractTextPattern, '');
}

// eslint-disable-next-line consistent-return
export function decodeHtml(html?: string) {
  if (html) {
    const div = document.createElement('div');
    div.insertAdjacentHTML('afterbegin', html);

    return div.innerText;
  }
}

export async function createNewMission({
  appId,
  scenarioId,
  order
}: Pick<AppParam, 'appId' | 'scenarioId'> & { order?: number }) {
  const { data } = await coreApi.post<Mission | CommonResponseModel<Mission>>(
    `/apps/${appId}/scenarios/${scenarioId}/missions`,
    {
      name: '새 미션',
      description: '<p></p>',
      isVisibleDefault: !order,
      isLockedDefault: false,
      order
    },
    MISSION_API_VERSION_HEADER
  );

  return extractCommonResponseData(data);
}

interface DuplicateMissionParams extends Pick<AppParam, 'appId' | 'scenarioId'> {
  mission: Mission;
  order?: number;
}

interface DuplicateQuestParams extends Pick<AppParam, 'appId'> {
  missionId: number;
  quest: Quest;
}

async function createQuestDuplicate({
  appId,
  missionId,
  quest
}: DuplicateQuestParams): Promise<Quest> {
  const { data } = await coreApi.post<Quest | CommonResponseModel<Quest>>(
    `/apps/${appId}/missions/${missionId}/quests`,
    buildQuestDuplicatePayload(quest),
    MISSION_API_VERSION_HEADER
  );

  const createdQuest = extractQuestResponseData(data);

  if (!createdQuest) {
    throw new Error('생성된 퀘스트 응답에서 id를 찾지 못했습니다.');
  }

  return createdQuest;
}

async function updateQuestActionHandler(
  appId: AppParam['appId'],
  sourceQuest: Quest,
  targetQuestId: number,
  options: ActionHandlerRemapOptions
) {
  if (!sourceQuest.actionHandler) return;

  await coreApi.put(
    `/apps/${appId}/quests/${targetQuestId}/action`,
    cloneActionHandlerForDuplicate(sourceQuest.actionHandler, options),
    MISSION_API_VERSION_HEADER
  );
}

export async function duplicateQuest({
  appId,
  missionId,
  quest
}: DuplicateQuestParams): Promise<Quest> {
  const createdQuest = await createQuestDuplicate({ appId, missionId, quest });

  await updateQuestActionHandler(appId, quest, createdQuest.id, {
    questIdMap: new Map([[quest.id, createdQuest.id]])
  });

  return createdQuest;
}

export async function duplicateMission({
  appId,
  scenarioId,
  mission,
  order
}: DuplicateMissionParams): Promise<Mission> {
  const { data: createdMissionResponse } = await coreApi.post<
    Mission | CommonResponseModel<Mission>
  >(
    `/apps/${appId}/scenarios/${scenarioId}/missions`,
    buildMissionDuplicatePayload(mission, order),
    MISSION_API_VERSION_HEADER
  );
  const createdMission = extractCommonResponseData(createdMissionResponse);

  const questIdMap = new Map<number, number>();
  const sourceQuests = mission.quests ?? [];

  const createdQuests = await sourceQuests.reduce<Promise<Quest[]>>(async (promise, quest) => {
    const previousQuests = await promise;
    const createdQuest = await createQuestDuplicate({
      appId,
      missionId: createdMission.id,
      quest
    });

    questIdMap.set(quest.id, createdQuest.id);
    return [...previousQuests, createdQuest];
  }, Promise.resolve([]));

  const missionIdMap = new Map([[mission.id, createdMission.id]]);

  await Promise.all(
    sourceQuests.map((sourceQuest, index) => {
      const targetQuest = createdQuests[index];
      if (!targetQuest) return Promise.resolve();

      return updateQuestActionHandler(appId, sourceQuest, targetQuest.id, {
        questIdMap,
        missionIdMap
      });
    })
  );

  return {
    ...createdMission,
    quests: createdQuests
  };
}
